/**
 * ApiImage — Robust image component for API-sourced images in SPAs.
 *
 * IMPORTANT: This is a drop-in replacement for <img>.
 * It does NOT add any wrapper elements — your existing CSS/className works unchanged.
 *
 * Fixes:
 * 1. Intermittent load on client-side navigation → absolute URL conversion
 * 2. Error fallback race condition → ref-based guard instead of dataset
 * 3. getImageSrcSet off-by-one bug → slice(14) not slice(13)
 * 4. getImageUrl endsWith(fallback) false-positive → strict equality only
 */

import { forwardRef, useMemo, useRef, useCallback } from 'react';

// ─── URL Utilities ────────────────────────────────────────────────────────────

export function ensureAbsoluteSrc(src) {
  if (!src || typeof src !== 'string') return '';
  if (
    src.startsWith('http://') ||
    src.startsWith('https://') ||
    src.startsWith('data:') ||
    src.startsWith('//')
  ) return src;

  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}${src.startsWith('/') ? '' : '/'}${src}`;
  }

  const base = (typeof process !== 'undefined' && process.env?.PUBLIC_URL) || '';
  return `${base}${src.startsWith('/') ? '' : '/'}${src}`;
}

function cloudinaryOptimizedUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cloudinary.com') || url.includes('/f_auto')) return url;
  const uploadIdx = url.indexOf('/upload/');
  if (uploadIdx === -1) return url;
  const insertAt = uploadIdx + 8;
  return url.slice(0, insertAt) + 'f_auto,q_auto/' + url.slice(insertAt);
}

const LEGACY_INVALID_URLS = /^(\.\.?\/)?subcategory-(round-neck|polo)(\.(jpg|jpeg|png|webp))?$/i;

/**
 * BUG FIX: removed url.endsWith(fallback) — too broad, caused false positives
 * for any image whose filename ends with the fallback's filename.
 */
export function getImageUrl(image, updatedAt = null, fallback = '/hero.png', webp = true) {
  if (!image) return fallback;

  let url;
  if (typeof image === 'string') {
    url = image;
  } else if (image?.secure_url) {
    url = image.secure_url;
  } else if (image?.url) {
    url = image.url;
  } else {
    return fallback;
  }

  if (!url) return fallback;
  if (url === fallback) return url; // FIX: was || url.endsWith(fallback)

  const barePath = url.replace(/^\//, '').split('?')[0];
  if (LEGACY_INVALID_URLS.test(barePath)) return fallback;

  if (webp) url = cloudinaryOptimizedUrl(url);

  if (updatedAt) {
    try {
      const ts = updatedAt instanceof Date ? updatedAt.getTime() : new Date(updatedAt).getTime();
      if (!isNaN(ts)) {
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}v=${ts}`;
      }
    } catch (e) {
      console.warn('[getImageUrl] Failed to parse updatedAt:', e);
    }
  }

  return url;
}

/**
 * BUG FIX: slice(13) → slice(14)
 * 'f_auto,q_auto/' is 14 chars. slice(13) left a leading '/' producing
 * double-slash URLs: .../upload/w_320,c_scale,f_auto,q_auto//v1234.jpg
 */
export function getImageSrcSet(imageUrl, widths = [320, 640, 960, 1280]) {
  if (!imageUrl || !imageUrl.includes('cloudinary.com')) return '';
  const uploadIdx = imageUrl.indexOf('/upload/');
  if (uploadIdx === -1) return '';

  const prefix = imageUrl.slice(0, uploadIdx + 8);
  let rest = imageUrl.slice(uploadIdx + 8);

  if (rest.startsWith('f_auto,q_auto/')) rest = rest.slice(14); // FIX: was 13

  return widths
    .map(w => `${prefix}w_${w},c_scale,f_auto,q_auto/${rest} ${w}w`)
    .join(', ');
}

export function getProductImageUrl(images, updatedAt = null, fallback = '/placeholder-jewelry.svg') {
  if (Array.isArray(images)) {
    return images.map(img => getImageUrl(img, updatedAt, fallback));
  }
  return getImageUrl(images, updatedAt, fallback);
}

// ─── ApiImage Component ───────────────────────────────────────────────────────

/**
 * Drop-in <img> replacement — NO wrapper element added.
 * Your existing className and CSS work exactly as before.
 *
 * IMPORTANT — to guarantee remount when src changes (fixes navigation issues),
 * always pass key={src} at the call site:
 *
 *   <ApiImage key={imageUrl} src={imageUrl} className="hero-img" alt="..." />
 *
 * This is the correct React pattern. A key inside forwardRef has no effect.
 */
const ApiImage = forwardRef(function ApiImage(
  {
    src,
    fallback = '/hero.png',
    alt = '',
    className = '',
    loading = 'lazy',
    decoding = 'async',
    width,
    height,
    fetchPriority,
    onLoad,
    onError,
    style,
    ...rest
  },
  ref
) {
  const absoluteSrc      = useMemo(() => ensureAbsoluteSrc(src || fallback), [src, fallback]);
  const absoluteFallback = useMemo(() => ensureAbsoluteSrc(fallback), [fallback]);

  const erroredRef = useRef(false);
  const prevSrcRef = useRef(absoluteSrc);

  if (prevSrcRef.current !== absoluteSrc) {
    prevSrcRef.current = absoluteSrc;
    erroredRef.current = false;
  }

  const handleError = useCallback((e) => {
    if (!erroredRef.current) {
      erroredRef.current = true;
      e.currentTarget.src = absoluteFallback;
    }
    onError?.(e);
  }, [absoluteFallback, onError]);

  if (!absoluteSrc) return null;

  return (
    <img
      ref={ref}
      src={absoluteSrc}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      width={width}
      height={height}
      fetchPriority={fetchPriority}
      onLoad={onLoad}
      onError={handleError}
      style={style}
      {...rest}
    />
  );
});

export default ApiImage;